/* eslint-disable @typescript-eslint/no-explicit-any */
// Read-only view of the No-Dues form exactly as it was filed (by the employee, or
// by HR on their behalf), with every clearing department's items shown inside the
// same view. Opened from the Finance screens (recovery heads, Establishment
// managers, the nodal) so they never have to leave the request to see the form.
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import {
  PageHeader,
  SectionCard,
  WarningBanner,
  EmptyState,
} from "./shared/components";
import { NoDuesFormSections } from "./shared/NoDuesFormSections";
import {
  getNoDuesFormDetails,
  getNoDuesSettlementDetail,
  getResignationDetails,
  formatDate,
  formatINR,
  NoDuesSettlementDepartment,
} from "./services/eSeparation.service";

const NoDuesFormView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const routes = all_routes;

  const state = (location.state as any) || {};
  const requestId: string = state.request_id || (params.id ? decodeURIComponent(params.id) : "");

  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<any>(null);
  const [parent, setParent] = useState<any>(null);
  const [parentId, setParentId] = useState<string>("");
  const [departments, setDepartments] = useState<NoDuesSettlementDepartment[]>([]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const [nd, settle] = await Promise.all([
        getNoDuesFormDetails(requestId),
        getNoDuesSettlementDetail(requestId),
      ]);
      if (!active) return;
      const rec = nd.data || null;
      setForm(rec?.details || null);
      const pid = rec?.request_id || settle.data?.request?.orig_request_id || "";
      setParentId(pid);
      setDepartments(settle.data?.departments || []);
      if (pid) {
        const p = await getResignationDetails(pid);
        if (active) setParent(p.data || null);
      }
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [requestId]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <p className="text-muted mt-3 mb-0">Loading No-Dues form…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content pb-5">
        <PageHeader
          title="No-Dues Form"
          subtitle={`${requestId}${parent?.emp_name ? ` · ${parent.emp_name} (${parent.emp_no})` : ""}`}
          right={
            parentId ? (
              <button
                className="btn btn-outline-primary"
                onClick={() =>
                  navigate(routes.Esepration.approvalDetail.replace(":id", parentId), {
                    state: { request_id: parentId, type: "Intimation", role: "Employee", viewOnly: true },
                  })
                }
              >
                <i className="ti ti-file-text me-1" />
                View Resignation Form
              </button>
            ) : undefined
          }
          onExit={() => navigate(-1)}
        />

        {form && Object.keys(form).length > 0 ? (
          <NoDuesFormSections form={form} startNumber={1} loansOverride={parent?.outstanding_loans} />
        ) : (
          <EmptyState
            icon="ti-file-off"
            title="No No-Dues form recorded"
            message="The No-Dues form for this request has no details."
          />
        )}

        {/* Items filled in by every clearing department — shown right here in the
            form view, cleared departments included. */}
        <SectionCard
          index={4}
          title="Departments' No-Dues Items"
          subtitle="What each clearing department reported, including those that cleared with nothing due."
        >
          {!departments.length ? (
            <WarningBanner type="info" message="No department clearances are recorded yet." />
          ) : (
            <div className="d-flex flex-column gap-3">
              {departments.map((d) => {
                const st = String(d.status).toUpperCase();
                const color = st === "CLEARED" ? "#049123" : st === "REJECTED" ? "#FF0F0F" : "#BA7517";
                const label = st === "CLEARED" ? "Cleared" : st === "REJECTED" ? "Dues Found" : "Pending";
                const total = d.items.reduce((sum, i) => sum + (Number(i.amount_due) || 0), 0);
                return (
                  <div key={d.id} className="p-3 rounded-3 border bg-light" style={{ borderLeft: `4px solid ${color}` }}>
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div>
                        <div className="fw-bold text-dark">{d.department_name}</div>
                        <small className="text-muted">
                          {d.approver_name || d.approver_emp_id || "—"}
                          {d.approver_emp_id ? ` (${d.approver_emp_id})` : ""}
                          {d.acted_at ? ` · ${formatDate(d.acted_at)}` : ""}
                        </small>
                      </div>
                      <span className="px-3 py-1 rounded-pill fw-medium" style={{ fontSize: 12, color, background: `${color}18` }}>
                        {label}
                      </span>
                    </div>
                    {d.remarks && (
                      <div className="small mt-2 text-secondary bg-white p-2 rounded border fst-italic">&quot;{d.remarks}&quot;</div>
                    )}
                    {d.items.length > 0 ? (
                      <div className="table-responsive mt-2">
                        <table className="table table-sm align-middle mb-0 bg-white">
                          <thead>
                            <tr>
                              <th>Item</th>
                              <th>Comments</th>
                              <th className="text-end" style={{ width: 130 }}>Amount Due</th>
                            </tr>
                          </thead>
                          <tbody>
                            {d.items.map((it, k) => (
                              <tr key={k}>
                                <td>{it.item_name}</td>
                                <td className="text-muted small">{it.comments || "—"}</td>
                                <td className="text-end">{formatINR(Number(it.amount_due))}</td>
                              </tr>
                            ))}
                            <tr>
                              <td colSpan={2} className="fw-semibold text-end">Department total</td>
                              <td className="text-end fw-semibold">{formatINR(total)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="small text-muted mt-2">
                        {st === "CLEARED" ? "No dues reported — cleared." : "No checklist items recorded."}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
};

export default NoDuesFormView;
